import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Lightning, Truck, ShieldCheck, ArrowRight, Package, Star } from "@phosphor-icons/react";

export default function ShopDevices({ standalone = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [activeSub, setActiveSub] = useState("all");
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/telecom/shop/catalog")
      .then(({ data }) => { setCategories(data.categories || []); setProducts(data.products || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const topCats = useMemo(() => categories.filter((c) => !c.parent_id), [categories]);
  const subsOf = (cid) => categories.filter((c) => c.parent_id === cid);
  const featured = useMemo(() => products.filter((p) => p.featured).slice(0, 6), [products]);

  const catalogJsonLd = useMemo(() => (products.length ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "iYeneh Phone Devices",
    itemListElement: products.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        name: p.name,
        image: p.image || undefined,
        description: p.description || undefined,
        url: `https://iyeneh.com/shop/${p.slug || p.id}`,
        brand: { "@type": "Brand", name: "iYeneh" },
        offers: {
          "@type": "Offer",
          priceCurrency: "USD",
          price: (p.price_cents / 100).toFixed(2),
          availability: (typeof p.stock === "number" && p.stock <= 0) ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
        },
      },
    })),
  } : null), [products]);

  usePageSeo({
    title: "Shop Phone Devices — SIM Cards, VoIP Phones, Routers & More | iYeneh Connect",
    description: "Buy pre-configured plug & play phone devices for your iYeneh line: SIM cards, smartphones, cordless & corded phones, VoIP handsets, modems and routers. Ships ready to use with secure checkout.",
    canonicalPath: "/shop",
    image: products[0]?.image,
    jsonLd: catalogJsonLd,
  });

  const visibleProducts = useMemo(() => {
    if (activeCat === "all") return products;
    const catIds = [activeCat, ...subsOf(activeCat).map((s) => s.id)];
    let list = products.filter((p) => catIds.includes(p.category_id));
    if (activeSub !== "all") list = list.filter((p) => p.category_id === activeSub);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, categories, activeCat, activeSub]);

  const buy = (p) => navigate(`/checkout?device=${p.id}`);

  const Card = ({ p }) => {
    const out = typeof p.stock === "number" && p.stock <= 0;
    return (
      <div data-testid={`product-${p.id}`} className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card">
        <Link to={`/shop/${p.slug || p.id}`} className="relative block aspect-square w-full overflow-hidden bg-secondary">
          {p.featured && <span className="absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground"><Star size={11} weight="fill" /> Featured</span>}
          {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center text-muted-foreground/40"><Package size={48} /></div>}
        </Link>
        <div className="flex flex-1 flex-col p-5">
          <Link to={`/shop/${p.slug || p.id}`} className="font-display text-lg font-semibold hover:text-primary">{p.name}</Link>
          {p.description ? <p className="mt-1 flex-1 text-sm text-muted-foreground">{p.description}</p> : <div className="flex-1" />}
          {out && <span className="mt-2 inline-flex w-fit rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-semibold text-red-500">Out of stock</span>}
          <div className="mt-4 flex items-center justify-between">
            <span className="font-mono-metric text-2xl font-bold">{money(p.price_cents, "USD")}</span>
            <button onClick={() => buy(p)} disabled={busy === p.id || out} data-testid={`buy-${p.id}`}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-60">
              {busy === p.id ? "Redirecting…" : "Buy now"} <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const content = (
    <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 sm:px-6" data-testid="shop-devices-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Shop Phone Devices</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">Pre-configured, plug &amp; play equipment for your iYeneh line — SIM cards, smartphones, cordless &amp; corded phones, VoIP handsets, modems and routers.</p>
      </div>

      {activeCat === "all" && featured.length > 0 && (
        <div data-testid="shop-featured" className="space-y-4">
          <div className="flex items-center gap-2">
            <Star size={20} weight="fill" className="text-primary" />
            <h2 className="font-display text-xl font-semibold">Featured &amp; Best Sellers</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((p) => <Card key={p.id} p={p} />)}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="shop-category-tabs">
        <button onClick={() => { setActiveCat("all"); setActiveSub("all"); }} data-testid="shop-cat-all"
          className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${activeCat === "all" ? "bg-primary text-primary-foreground" : "border border-border bg-card hover:border-primary"}`}>
          All Devices
        </button>
        {topCats.map((c) => (
          <button key={c.id} onClick={() => { setActiveCat(c.id); setActiveSub("all"); }} data-testid={`shop-cat-${c.slug}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${activeCat === c.id ? "bg-primary text-primary-foreground" : "border border-border bg-card hover:border-primary"}`}>
            {c.name}
          </button>
        ))}
      </div>

      {activeCat !== "all" && subsOf(activeCat).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="shop-subcategory-chips">
          <button onClick={() => setActiveSub("all")} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${activeSub === "all" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}>All</button>
          {subsOf(activeCat).map((s) => (
            <button key={s.id} onClick={() => setActiveSub(s.id)} data-testid={`shop-sub-${s.slug}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${activeSub === s.id ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{s.name}</button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading devices…</p>
      ) : visibleProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/50 py-16 text-center" data-testid="shop-empty">
          <Package size={40} className="text-muted-foreground/60" />
          <p className="mt-3 font-semibold">No products here yet</p>
          <p className="mt-1 text-sm text-muted-foreground">We're adding devices to this category soon. Check back shortly.</p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visibleProducts.map((p) => <Card key={p.id} p={p} />)}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        {[{ i: Truck, t: "Ships to your door", d: "Pre-configured and ready." },
          { i: Lightning, t: "Zero setup", d: "Plug in and start talking." },
          { i: ShieldCheck, t: "Secure checkout", d: "Encrypted card payment." }].map((b) => (
          <div key={b.t} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4">
            <b.i size={20} className="mt-0.5 shrink-0 text-primary" />
            <div><p className="text-sm font-semibold">{b.t}</p><p className="text-xs text-muted-foreground">{b.d}</p></div>
          </div>
        ))}
      </div>
    </div>
  );

  if (!standalone) return content;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid="shop-nav-logo" className="flex items-center gap-2">
            <img src="/icons/icon-512.png" alt="iYeneh Connect" className="h-9 w-9" />
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>iYeneh Connect</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/pricing" className="hidden text-sm font-semibold text-muted-foreground hover:text-foreground sm:block">Phone Plans</Link>
            {user ? (
              <Link to="/app" data-testid="shop-nav-account" className="rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground">My Account</Link>
            ) : (
              <>
                <Link to="/login" data-testid="shop-nav-signin" className="hidden text-sm font-semibold text-muted-foreground hover:text-foreground sm:block">Sign In</Link>
                <Link to="/register" data-testid="shop-nav-register" className="rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground">Create Free Account</Link>
              </>
            )}
          </div>
        </div>
      </header>
      {content}
    </div>
  );
}
